import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Card,
  CardContent
} from '@mui/material';
import {
  Security,
  Refresh,
  Send,
  Lock,
  Email,
  Timer,
  ArrowBack
} from '@mui/icons-material';
import superAdminService from '../services/superAdminService';

interface OTPVerificationProps {
  email: string;
  onVerified: (token: string, user: any) => void;
  onCancel: () => void;
}

const OTPVerification: React.FC<OTPVerificationProps> = ({ email, onVerified, onCancel }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [expiresIn, setExpiresIn] = useState(10);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const otpSentRef = useRef(false); // Prevent duplicate sends on React 18 StrictMode

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle input change
  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only last digit
    setOtp(newOtp);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (index === 5 && value) {
      const fullOtp = [...newOtp.slice(0, 5), value].join('');
      if (fullOtp.length === 6) {
        handleVerify(fullOtp);
      }
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).replace(/\D/g, '').split('');
    const newOtp = [...otp];
    pastedData.forEach((digit, i) => {
      if (i < 6) newOtp[i] = digit;
    });
    setOtp(newOtp);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newOtp.findIndex((d) => !d);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();

    // Auto-submit if complete
    if (pastedData.length === 6) {
      handleVerify(pastedData.join(''));
    }
  };

  // Send OTP
  const handleSendOTP = async () => {
    setResendLoading(true);
    setError(null);
    try {
      const result = await superAdminService.sendOTP(email);
      setSuccess(`Verification code sent! Valid for ${result.expiresIn} minutes.`);
      setExpiresIn(result.expiresIn);
      setTimeLeft(result.expiresIn * 60);
      // Clear OTP fields
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      // Extract detailed error message from API response
      const errorData = err.response?.data;
      let errorMessage = 'Failed to send verification code';

      if (errorData) {
        if (errorData.error) {
          errorMessage = errorData.error;
        }
        if (errorData.details) {
          errorMessage += ` (${errorData.details})`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      console.error('Send OTP error:', err);
      setError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  // Verify OTP
  const handleVerify = async (code?: string) => {
    const fullOtp = code || otp.join('');

    if (fullOtp.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    if (timeLeft === 0) {
      setError('Code has expired. Please request a new one.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await superAdminService.verifyOTP(email, fullOtp);
      setSuccess('Verification successful!');
      onVerified(result.token, result.user);
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
      // Clear OTP fields on error
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Send OTP on mount (only once)
  useEffect(() => {
    if (!otpSentRef.current) {
      otpSentRef.current = true;
      handleSendOTP();
    }
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 500, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}
            >
              <Security sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Two-Factor Authentication
            </Typography>
            <Typography color="text.secondary">
              Enter the 6-digit code sent to your email
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Email Display */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, gap: 1 }}>
            <Email color="primary" />
            <Typography variant="body1" fontWeight="medium">
              {email}
            </Typography>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Timer */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Chip
              icon={<Timer />}
              label={timeLeft > 0 ? `Expires in: ${formatTime(timeLeft)}` : 'Code expired'}
              color={timeLeft > 60 ? 'primary' : timeLeft > 0 ? 'warning' : 'error'}
              variant="outlined"
              size="medium"
            />
          </Box>

          {/* OTP Input */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, textAlign: 'center' }}>
              Enter verification code
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1.5,
                mb: 2
              }}
              onPaste={handlePaste}
            >
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      padding: '8px'
                    }
                  }}
                  sx={{
                    width: 50,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: digit ? 'primary.light' : 'background.paper',
                      '& fieldset': {
                        borderWidth: digit ? 2 : 1,
                        borderColor: digit ? 'primary.main' : 'grey.300'
                      }
                    }
                  }}
                  variant="outlined"
                  disabled={loading || timeLeft === 0}
                  autoFocus={index === 0}
                />
              ))}
            </Box>
          </Box>

          {/* Verify Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => handleVerify()}
            disabled={loading || otp.join('').length !== 6 || timeLeft === 0}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Lock />}
            sx={{
              py: 1.5,
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)'
              }
            }}
          >
            {loading ? 'Verifying...' : 'Verify & Login'}
          </Button>

          {/* Resend */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
            <Button
              variant="text"
              startIcon={<Refresh />}
              onClick={handleSendOTP}
              disabled={resendLoading || timeLeft > 540} // Can resend after 1 minute
              size="small"
            >
              {resendLoading ? 'Sending...' : 'Resend Code'}
            </Button>
          </Box>

          {/* Back to Login */}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="text"
              startIcon={<ArrowBack />}
              onClick={onCancel}
              size="small"
              color="inherit"
            >
              Back to Login
            </Button>
          </Box>

          {/* Security Info */}
          <Alert severity="info" sx={{ mt: 3 }} icon={<Security />}>
            <Typography variant="body2">
              For security, super admins must verify their identity with a code sent to their email. 
              The code expires in {expiresIn} minutes.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OTPVerification;
